<template lang="pug">
	.side-menu
		ul.menu(v-if="user")
			template(v-for="item in menuItems", :key="item.name || item.title")
				li(v-if="!item.permission || user.permissions[item.permission]", :class="{'expanded active':item===activeMenu}")
					.menu-row(:class="{'has-children': item.children}")
						router-link.menu-link(v-if="item.link", :to="item.link")
							i.la(:class="'la-'+item.icon")
							| {{ item.title }}
							new-feature(v-if="item.new")
						a.menu-link(v-else, href="#", @click.prevent="toggleMenu(item)", :aria-expanded="item===activeMenu")
							i.la(:class="'la-'+item.icon")
							| {{ item.title }}
							span.toggle-arrow.la.la-angle-up(v-if="item.children")
						menu-attention-badge(v-if="!item.children || item!==activeMenu", v-bind="attention(item)", :to="attentionLink(item)", @click="toggleMenu(item)")
					ul(v-if="item.children")
						template(v-for="subItem in item.children", :key="subItem.name")
							li(v-if="!subItem.permission || user.permissions[subItem.permission]")
								.menu-row
									router-link.menu-link(:to="subItem.link || {name: subItem.name}") {{ subItem.title }}
										new-feature(v-if="subItem.new")
									menu-attention-badge(v-if="item===activeMenu", v-bind="attention(subItem)", :to="attentionLink(subItem)")
</template>
<script>
import http from '../api/http';
import MenuAttentionBadge from './MenuAttentionBadge.vue';
import { attentionItems, getMenuAttention } from '../utils/menuAttention';
export default {
  name: 'SideMenu',
  components: { MenuAttentionBadge },
  methods: {
    attention(item) {
      return getMenuAttention({ item, counts: this.attentionCounts, permissions: this.user.permissions });
    },
    attentionLink(item) {
      const definition = attentionItems[item.name];
      return definition ? { name: item.name, query: { attention: definition.attention } } : null;
    },
    scheduleAttentionRefresh() {
      clearTimeout(this.attentionRefreshTimer);
      this.attentionRefreshTimer = setTimeout(() => this.refreshAttentionCounts(), 300);
    },
    onAttentionFocus() {
      if (document.visibilityState === 'visible') this.scheduleAttentionRefresh();
    },
    async refreshAttentionCounts() {
      if (this.attentionBusy) {
        this.attentionRefreshPending = true;
        return;
      }
      if (document.visibilityState === 'hidden') return;
      this.attentionBusy = true;
      try {
        const response = await http.get('notification/getAttentionCounts', { timeout: 15000 });
        if (response.data.err !== 0) throw new Error('Attention counts unavailable');
        Object.assign(this.attentionCounts, response.data.data);
      } catch {
        // Background count refreshes must not produce notification toasts.
        this.attentionCounts.offers = null;
        this.attentionCounts.placements = null;
        this.attentionCounts.advertisers = null;
      }
      this.attentionBusy = false;
      if (this.attentionRefreshPending && this.attentionMounted) {
        this.attentionRefreshPending = false;
        this.scheduleAttentionRefresh();
      }
    },
    toggleMenu(item) {
      if (this.activeMenu === item) {
        this.activeMenu = null;
      } else {
        this.activeMenu = item;
      }
    },
  },
  mounted() {
    let user = this.$store.state.user;
    if (user.config) {
      for (let feature in user.config) {
        user.permissions[`feature::${feature || ''}`] = true;
      }
    }
    this.user = user;
    this.attentionMounted = true;
    this.refreshAttentionCounts();
    this.attentionInterval = setInterval(() => this.refreshAttentionCounts(), 60000);
    this.removeAttentionRouteHook = this.$router.afterEach(() => this.scheduleAttentionRefresh());
    window.addEventListener('octava:attention-changed', this.scheduleAttentionRefresh);
    window.addEventListener('focus', this.onAttentionFocus);
    document.addEventListener('visibilitychange', this.onAttentionFocus);
  },
  beforeUnmount() {
    this.attentionMounted = false;
    clearInterval(this.attentionInterval);
    clearTimeout(this.attentionRefreshTimer);
    this.removeAttentionRouteHook?.();
    window.removeEventListener('octava:attention-changed', this.scheduleAttentionRefresh);
    window.removeEventListener('focus', this.onAttentionFocus);
    document.removeEventListener('visibilitychange', this.onAttentionFocus);
  },
  data() {
    return {
      user: null,
      attentionCounts: { offers: null, placements: null, advertisers: null },
      attentionBusy: false,
      attentionRefreshPending: false,
      attentionMounted: false,
      attentionRefreshTimer: null,
      attentionInterval: null,
      removeAttentionRouteHook: null,
      activeMenu: null,
      menuItems: [
        // {
        // 	title: 'Dashboard',
        // 	icon: 'home',
        // 	name: 'dashboard',
        // 	link: '/app/dashboard',
        // },
        {
          title: 'Demand',
          icon: 'exchange-alt',
          children: [
            {
              title: 'Advertiser',
              name: 'advertiser-list',
              link: '/app/advertisers',
              permission: 'advertisers',
            },
            {
              title: 'Offers',
              name: 'offer-list',
              link: '/app/offers',
              permission: 'offers',
            },
            {
              title: 'External Offers',
              name: 'external-offers',
              link: '/app/external-offers',
              permission: 'external_offers',
            },
            {
              title: 'MMP Accounts',
              name: 'mmp-account-list',
              link: '/app/mmp-accounts',
            },
            // {
            // 	title: 'Apps',
            // 	name: 'apps',
            // 	link: '/app/apps',
            // 	permission: 'admin_only',
            // }
          ],
        },
        {
          title: 'Supply',
          icon: 'exchange-alt',
          children: [
            {
              title: 'Publishers',
              name: 'publisher-list',
              link: '/app/publishers',
              permission: 'publishers',
            },
            {
              title: 'Placements',
              name: 'placement-list',
              link: '/app/placements',
              permission: 'placements',
            },
            {
              title: 'External Placements',
              name: 'external-placements',
              link: '/app/external-placements',
            },
          ],
        },
        {
          title: 'Performance',
          icon: 'area-chart',
          children: [
            // {
            // 	title: 'Statistics',
            // 	name: 'statistics',
            // 	link: '/app/statistics',
            // },
            {
              title: 'Reports',
              name: 'reports',
              link: '/app/reports',
            },
            {
              title: 'Conversions',
              name: 'conversions',
              link: '/app/conversions',
            },
            {
              title: 'Publisher Conv.',
              name: 'pub_conversions',
              link: '/app/pub_conversions',
            },
            {
              title: 'Events',
              name: 'events',
              link: '/app/events',
            },
            {
              title: 'Rejections',
              name: 'rejected',
              link: '/app/rejected',
            },
            // {
            // 	title: 'KPI Rules',
            // 	name : 'kpi-rules',
            // 	link : '/app/kpi-rules',
            // },
          ],
        },
        {
          title: 'Lookup',
          icon: 'search',
          children: [
            // {
            // 	title: 'Statistics',
            // 	name: 'statistics',
            // 	link: '/app/statistics',
            // },
            // {
            // 	title: 'Clicks',
            // 	name: 'clicks',
            // 	link: '/app/clicks',
            // },
            {
              title: 'Clicks',
              name: 'click-search',
              link: '/app/click-search',
            },
            {
              title: 'Post-attribution reports',
              name: 'pa-reports',
              link: '/app/pa-reports',
            },
            {
              title: 'Conversion Reports',
              name: 'conversion-reports',
              link: '/app/conversion-reports',
              new: true,
            },
            {
              title: 'External Cost Data',
              name: 'external-cost-data',
              link: '/app/external-cost-data',
              new: true,
              permission: 'feature::externalCostData',
            },
            {
              title: 'URL Scanner',
              name: 'url-scanner',
              link: '/app/url-scanner',
              new: true
            },
            // {
            // 	title: 'Subsources',
            // 	icon: 'list',
            // 	name: 'subsources',
            // 	link: '/app/subsources',
            // },
          ],
        },
        {
          title: 'Finance',
          icon: 'dollar',
          permission: 'admin_only',
          children: [
            {
              title: 'Legal Entities',
              name: 'legal-entity-list',
              link: '/app/finance/legal-entities',
            },
            {
              title: 'Advertisers',
              name: 'finance-advertisers',
              link: '/app/finance/advertisers',
            },
            {
              title: 'Publishers',
              name: 'finance-publishers',
              link: '/app/finance/publishers',
            },
            {
              title: 'Summary',
              name: 'finance-list',
              link: '/app/finance/summary-list',
            },
            // {
            // 	title: 'Documents',
            // 	name: 'documents',
            // 	link: '/app/finance/documents'
            // },
          ],
        },
        // {
        //   title: 'Location',
        //   icon: 'map-marked-alt',
        //   name: 'data',
        //   children: [
        //     // {
        //     // 	title: 'SSPs',
        //     // 	name: 'ssp',
        //     // 	link: '/app/ssp',
        //     // },
        //     // {
        //     // 	title: 'Exchanges',
        //     // 	name: 'exchanges',
        //     // 	link: '/app/exchanges',
        //     // },
        //     {
        //       title: 'Countries',
        //       name: 'countries',
        //       link: '/app/countries',
        //     },
        //     {
        //       title: 'Regions',
        //       name: 'regions',
        //       link: '/app/regions',
        //     },
        //     {
        //       title: 'Cities',
        //       name: 'cities',
        //       link: '/app/cities',
        //     },
        //     {
        //       title: 'IPs',
        //       name: 'ipinfo',
        //       link: '/app/ipinfo',
        //     },
        //     // {
        //     // 	title: 'Carriers',
        //     // 	name: 'carriers',
        //     // 	link: '/app/carriers',
        //     // },
        //     // {
        //     // 	title: 'Sources',
        //     // 	name: 'sources',
        //     // 	link: '/app/sources',
        //     // },
        //     // {
        //     // 	title: 'Devices',
        //     // 	name: 'devices',
        //     // 	link: '/app/devices',
        //     // },
        //     // {
        //     // 	title: 'App Preview',
        //     // 	name: 'app-preview',
        //     // 	link: '/app/app-preview',
        //     // },
        //     // {
        //     // 	title: 'OS',
        //     // 	name: 'os',
        //     // 	link: '/app/os',
        //     // },
        //     // {
        //     // 	title: 'Browsers',
        //     // 	name: 'browsers',
        //     // 	link: '/app/browsers',
        //     // },
        //     // {
        //     // 	title: 'Browser Versions',
        //     // 	name: 'browser-versions',
        //     // 	link: '/app/browser-versions',
        //     // },
        //   ],
        // },

        {
          title: 'Settings',
          icon: 'cogs',
          permission: 'admin_only',
          children: [
            {
              title: 'Users',
              name: 'user-list',
              link: '/app/users',
            },
            {
              title: 'Offer Templates',
              name: 'offer-template-list',
              link: '/app/offer-templates',
            },
            {
              title: 'System Settings',
              name: 'settings',
              link: '/app/settings',
            },

            /**
             * @link https://app.clickup.com/t/86er6tj18 Fallback disabled for now
             */
            // {
            //   title: 'Smartlink',
            //   name: 'offer-fallback',
            //   link: '/app/fallback',
            //   permission: 'offers',
            // },

            {
              title: 'KPI Rules',
              name: 'kpi-rules',
              link: '/app/kpi-rules',
            },
            // {
            //   title: 'Terms',
            //   name: 'terms',
            //   link: '/app/terms',
            // },
            {
              title: 'Devices Blacklist',
              name: 'devices-blacklist',
              link: '/app/devices-blacklist',
              permission: 'feature::deviceBL',
            },
            {
              title: 'Reports',
              name: 'reports-settings',
              link: '/app/reports-settings',
            },
          ],
        },

        {
          title: 'Monitor',
          icon: 'server',
          permission: 'admin_only',
          children: [
            // {
            // 	title: 'Clicks',
            // 	name: 'monitor-clicks',
            // 	link: '/app/monitor',
            // },
            {
              title: 'Postback Log',
              name: 'postback-log',
              link: '/app/postback-log',
            },
            {
              title: 'Publisher Postback',
              name: 'publisher-postbacks',
              link: '/app/publisher-postbacks',
            },
            {
              title: 'Activity Log',
              name: 'activity-log',
              link: '/app/activity-log',
            },
            {
              title: 'KPI Rules Log',
              name: 'kpi-rule-log',
              link: '/app/kpi-rule-log',
            },
            // {
            // 	title: 'Request Storage',
            // 	name: 'monitor-request-storage',
            // 	link: '/app/request-storage',
            // },
          ],
        },
        {
          title: 'Support',
          icon: 'question',
          name: 'support',
          link: '/app/support',
        },
        // {
        // 	title: 'Documentation',
        // 	icon: 'book',
        // 	name: 'docs',
        // 	link: '/docs',
        // },
        // {
        // 	title: 'Settings',
        // 	icon: 'cogs',
        // 	name: 'settings',
        // 	link: '/app/settings',
        // },
      ],
    };
  },
};
</script>
